import { useEffect, useReducer, useState } from 'react';

import axios from 'axios';

import { SSO_CONFIG_PATH } from '../config/apiPaths';
import { getEnvironment } from '../utils/environment';
import { getUrls } from '../utils/url';

/**
 * Sso Config with versioning. CURRENT_VERSION must
 * be updated if this type changes.
 */
export interface SsoConfig {
    id: string;
    display_name: string;
}

interface SsoConfigState {
    loading: boolean;
    error?: Error;
    data?: SsoConfig[];
}

const initialState = {
    loading: true,
    error: undefined,
    data: undefined
};

type Action = { type: 'loading' } | { type: 'success'; payload: SsoConfig[] } | { type: 'error'; payload: Error };

const reducer = (state: SsoConfigState, action: Action) => {
    switch (action.type) {
        case 'success':
            return { ...state, loading: false, data: action.payload };
        case 'error':
            return { ...state, loading: false, error: action.payload };
        case 'loading':
        default:
            return initialState;
    }
};

/**
 * Get basic sso config and cache in local storage
 */
export const useSsoConfig = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [ssoConfig, setSsoConfig] = useState<SsoConfig[] | undefined>(undefined);

    useEffect(() => {
        const { api } = getUrls();
        const { account } = getEnvironment();

        const url = `${api}${SSO_CONFIG_PATH}`;

        if (ssoConfig) {
            dispatch({ type: 'success', payload: ssoConfig });
            return;
        }

        const getSsoConfig = async () => {
            dispatch({ type: 'loading' });

            try {
                const { data: ssoConfigList } = await axios.get(url);

                dispatch({ type: 'success', payload: ssoConfigList });
                setSsoConfig(ssoConfigList);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);

                dispatch({ type: 'error', payload: err as Error });
            }
        };

        getSsoConfig();
    }, [ssoConfig, setSsoConfig]);

    return { ...state };
};
